import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Box,
  Divider,
  Snackbar,
  IconButton,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import apiService from "../components/ReasuableFuncations/api";
import StatusBadge from '../components/ReasuableFuncations/StatusBadge';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const DataCollection = () => {
  const [formType, setFormType] = useState("outcome");
  const [data, setData] = useState([]);
  const [projectSummary, setProjectSummary] = useState({
    totalProjects: 0,
    totalOngoing: 0,
    totalPending: 0,
    totalCompleted: 0,
  });
  const [selectedChart, setSelectedChart] = useState("total");

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState('');

  const [error, setError] = useState(null);
  const [userType, setUserType] = useState("");
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    activityname: "",
    description: "",
    dateAchieved: "",
    targetDate: "",
    status: "",
    target: { value: "", currency: "", symbol: "" },
    current: { value: "", currency: "", symbol: "" },
    resourceallocated: { value: "", currency: "", symbol: "" },
    justify: "",
    lessons: "",
    challenges: "",
    comments: "",
    percentageAchievement: "",
    type: "",
    indicators: [],
    reportingFrequency: "",
    baseline: { value: "", currency: "", symbol: "" },
    baselineDate: "",
    actualOutputDate: "",
  });

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    fetchDataCollection();
    if (formType === 'activity') {
      fetchProjectSummary();
    }
  }, [formType]);

  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
      setUserRole({ selectedOption, regionalNetworkOption, memberStateOption, countryOption });
    }
  }, []);

  const fetchDataCollection = async () => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (!userData) {
      console.error("User data not found");
      return;
    }
  
    const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
    let url = `/api/data-collection?type=${formType.toLowerCase()}`;
  
    try {
      let response;
      switch (selectedOption) {
        case 'Comesa':
          response = await apiService.get(url);
          break;
  
        case 'Regional Network':
          if (regionalNetworkOption) {
            response = await apiService.get(`${url}&regionalNetwork=${regionalNetworkOption}`);
          }
          break;
  
        case 'Member State':
          if (memberStateOption) {
            response = await apiService.get(`${url}&country=${countryOption}`);
          }
          break;
  
        default:
          throw new Error('Invalid selection');
      }
  
      if (!response || response.statusText !== "OK") {
        throw new Error("Failed to fetch data");
      }
  
      const fetchedData = response.data;
      setData(fetchedData);
  
      if (formType === 'activity') {
        updateProjectSummary(fetchedData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to fetch data");
    }
  };

  const fetchProjectSummary = async () => {
    try {
      const response = await apiService.get(`/api/data-collection?type=activity`);
      
      if (response && response.statusText !== 'OK') {
        throw new Error('Failed to fetch data');
      }
      
      const fetchedData = response.data;
      updateProjectSummary(fetchedData);
    } catch (error) {
      console.error('Error fetching project summary:', error);
      setError('Failed to fetch project summary');
    }
  };

  const handleOpenDialog = (description) => {
    setSelectedDescription(description);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDescription('');
  };

  const updateProjectSummary = (fetchedData) => {
    const totalOngoing = fetchedData.filter(item => item.status === "Ongoing").length;
    const totalPending = fetchedData.filter(item => item.status === "Pending").length;
    const totalCompleted = fetchedData.filter(item => item.status === "Completed").length;
    setProjectSummary({
      totalProjects: fetchedData.length,
      totalOngoing,
      totalPending,
      totalCompleted,
    });
  };

  const filteredActivityData = data.filter((item) => {
    if (formType !== "activity") return false;
    switch (selectedChart) {
      case "pending":
        return item.status === "Pending";
      case "ongoing":
        return item.status === "Ongoing";
      case "complete":
        return item.status === "Completed";
      default:
        return true;
    }
  });

  const formatMonetaryValue = (value) => {
    if (!value) return 'Not provided';
    if (typeof value === 'object') {
      return `${value.symbol || ''}${value.value || 0}`;
    }
    return value;
  };

  const formatValueOnly = (value) => {
    if (!value) return 'Not provided';
    if (typeof value === 'object') {
      return value.value || 0;
    }
    return value;
  };

  const handleEdit = (item) => {
    // Format monetary values correctly
    const formatMonetaryFields = (field) => ({
      value: field?.value?.toString() || "",
      currency: field?.currency || "USD",
      symbol: field?.symbol || "$"
    });

    // Format dates to YYYY-MM-DD for input fields
    const formatDate = (date) => {
      if (!date) return "";
      return new Date(date).toISOString().split('T')[0];
    };

    setItemToEdit(item);
    setEditFormData({
      // Basic fields
      name: item.name || "",
      activityname: item.activityname || "",
      description: item.description || "",
      
      // Dates
      dateAchieved: formatDate(item.dateAchieved),
      targetDate: formatDate(item.targetDate),
      baselineDate: formatDate(item.baselineDate),
      actualOutputDate: formatDate(item.actualOutputDate),
      
      // Status and type
      status: item.status || "",
      type: item.type || "",
      
      // Monetary values
      baseline: formatMonetaryFields(item.baseline),
      target: formatMonetaryFields(item.target),
      current: formatMonetaryFields(item.current),
      resourceallocated: formatMonetaryFields(item.resourceallocated),
      
      // Arrays
      indicators: Array.isArray(item.indicators) ? item.indicators : [],
      
      // Text fields
      justify: item.justify || "",
      lessons: item.lessons || "",
      challenges: item.challenges || "",
      comments: item.comments || "",
      
      // Other fields
      percentageAchievement: item.percentageAchievement || "",
      reportingFrequency: item.reportingFrequency || "",
      
      // Preserve project-related fields
      projectName: item.projectName,
      selectedOption: item.selectedOption,
      regionalNetworkOption: item.regionalNetworkOption,
      memberStateOption: item.memberStateOption,
      countryOption: item.countryOption
    });

    setOpenEditDialog(true);
  };

  const handleEditConfirm = () => {
    navigate('/data-collection', { state: { editData: itemToEdit } });
    setOpenEditDialog(false);
    setItemToEdit(null);
  };

  const handleUpdate = async () => {
    try {
      if (!itemToEdit?._id) {
        throw new Error("No item selected for update");
      }

      // For activity type, calculate cost variance
      let formattedResourceAllocated = editFormData.resourceallocated;
      if (itemToEdit.type === 'activity') {
        const variance = calculateCostVariance(
          editFormData.target.value,
          editFormData.current.value
        );
        
        // Remove % symbol when storing in database
        const numericVariance = parseFloat(variance.replace('%', ''));
        
        formattedResourceAllocated = {
          value: numericVariance,
          currency: editFormData.target.currency || "USD",
          symbol: editFormData.target.symbol || "$"
        };
      }

      const updateData = {
        ...editFormData,
        projectName: itemToEdit.projectName,
        type: itemToEdit.type,
        
        // Format monetary values
        target: {
          value: parseFloat(editFormData.target.value) || 0,
          currency: editFormData.target.currency || "USD",
          symbol: editFormData.target.symbol || "$"
        },
        current: {
          value: parseFloat(editFormData.current.value) || 0,
          currency: editFormData.current.currency || "USD",
          symbol: editFormData.current.symbol || "$"
        },
        resourceallocated: formattedResourceAllocated,
        
        // Other existing fields...
        // ...existing code...
      };

      const response = await apiService.put(
        `/api/data-collection/${itemToEdit._id}`,
        updateData
      );

      if (response.data) {
        // Update local data with formatted values
        setData(data.map(item => 
          item._id === itemToEdit._id ? 
          { ...item, ...updateData } : 
          item
        ));
        
        setSnackbarMessage("Item updated successfully!");
        setSnackbarSeverity("success");
        setOpenEditDialog(false);
        setItemToEdit(null);
        
        // Refresh data
        fetchDataCollection();
      }
    } catch (error) {
      console.error("Update error:", error);
      setSnackbarMessage(error.message || "Error updating item");
      setSnackbarSeverity("error");
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiService.delete(`/api/data-collection/${id}`);
      if (response.statusText === "OK") {
        setData(data.filter(item => item._id !== id));
        setSnackbarMessage("Item deleted successfully!");
        setSnackbarSeverity("success");
      } else {
        throw new Error("Failed to delete item");
      }
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbarMessage("Error deleting item");
      setSnackbarSeverity("error");
    }
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const canEditDelete = (item) => {
    if (!userRole) return false;
    
    switch (userRole.selectedOption) {
      case 'Comesa':
        return true; // Comesa users can edit/delete all items
      case 'Regional Network':
        return item.regionalNetworkOption === userRole.regionalNetworkOption;
      case 'Member State':
        return item.countryOption === userRole.countryOption;
      default:
        return false;
    }
  };

  const actionColumn = {
    header: <TableCell>Actions</TableCell>,
    cell: (item) => (
      <TableCell>
        {canEditDelete(item) && (
          <>
            <IconButton onClick={() => handleEdit(item)} color="primary">
              <EditIcon />
            </IconButton>
            <IconButton 
              onClick={() => handleDeleteClick(item)}
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </TableCell>
    )
  };

  const renderTable = () => {
    switch (formType) {
      case "outcome":
        return (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Outcome Name</TableCell>
                  <TableCell>Baseline Value</TableCell>
                  <TableCell>Target Value</TableCell>
                  <TableCell>Current Value</TableCell>
                  <TableCell>Outcome Indicator</TableCell>
                  <TableCell>Baseline Date</TableCell>
                  <TableCell>Target Date</TableCell>
                  <TableCell>Level of Achievement</TableCell>
                  <TableCell>Justification</TableCell>
                  <TableCell>Frequency of Reporting</TableCell>
                  <TableCell>Comments</TableCell>
                  {actionColumn.header}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.projectName?.projectName}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{formatValueOnly(item.baseline)}</TableCell>
                    <TableCell>{formatValueOnly(item.target)}</TableCell>
                    <TableCell>{formatValueOnly(item.current)}</TableCell>
                    <TableCell>{item.indicators?.join(', ')}</TableCell>
                    <TableCell>
                      {item.dateAchieved &&
                        new Date(item.dateAchieved).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {item.targetDate &&
                        new Date(item.targetDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{item.percentageAchievement}</TableCell>
                    <TableCell>
                      {item.justify ? (
                        item.justify.split(" ").length > 6 ? (
                          <>
                            {item.justify.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(item.justify)}>
                              More
                            </Button>
                          </>
                        ) : (
                          item.justify
                        )
                      ) : (
                        "No Justification"
                      )}
                    </TableCell>
                    <TableCell>{item.reportingFrequency}</TableCell>
                    <TableCell>
                      {item.comments ? (
                        item.comments.split(" ").length > 6 ? (
                          <>
                            {item.comments.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(item.comments)}>
                              More
                            </Button>
                          </>
                        ) : (
                          item.comments
                        )
                      ) : (
                        "No comments"
                      )}
                    </TableCell>
                    {actionColumn.cell(item)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case "output":
        return (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Output Name</TableCell>
                  <TableCell>Output Indicator</TableCell>
                  <TableCell>Baseline Value</TableCell>
                  <TableCell>Target Output</TableCell>
                  <TableCell>Actual Output</TableCell>
                  <TableCell>Baseline Date</TableCell>
                  <TableCell>Target Date</TableCell>
                  <TableCell>Actual Date</TableCell>
                  <TableCell>Level of Achievement</TableCell>
                  <TableCell>Justification</TableCell>
                  <TableCell>Comments</TableCell>
                  {actionColumn.header}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.projectName?.projectName}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.indicators?.join(', ')}</TableCell>
                    <TableCell>{formatValueOnly(item.baseline)}</TableCell>
                    <TableCell>{formatValueOnly(item.target)}</TableCell>
                    <TableCell>{formatValueOnly(item.current)}</TableCell>
                    <TableCell>
                      {item.dateAchieved &&
                        new Date(item.dateAchieved).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {item.targetDate &&
                        new Date(item.targetDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {item.actualOutputDate &&
                        new Date(item.actualOutputDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{item.percentageAchievement}</TableCell>
                    <TableCell>
                      {item.justify ? (
                        item.justify.split(" ").length > 6 ? (
                          <>
                            {item.justify.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(item.justify)}>
                              More
                            </Button>
                          </>
                        ) : (
                          item.justify
                        )
                      ) : (
                        "No Justification"
                      )}
                    </TableCell>
                    <TableCell>
                      {item.comments ? (
                        item.comments.split(" ").length > 6 ? (
                          <>
                            {item.comments.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(item.comments)}>
                              More
                            </Button>
                          </>
                        ) : (
                          item.comments
                        )
                      ) : (
                        "No comments"
                      )}
                    </TableCell>
                    {actionColumn.cell(item)}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case "activity":
        return (
          <>
            <Box
              sx={{
                display: "flex",
          flexDirection: { xs: "column", sm: "row" }, 
          justifyContent: "center",
          marginBottom: 4,
              }}
            >
              <Button
                variant={selectedChart === "total" ? "contained" : "outlined"}
                onClick={() => setSelectedChart("total")}
                sx={{ marginRight: 1,display: "flex",
                justifyContent: "center", }}
              >
                Total Projects ({projectSummary.totalProjects})
              </Button>
              <Button
                variant={selectedChart === "pending" ? "contained" : "outlined"}
                onClick={() => setSelectedChart("pending")}
                sx={{ marginRight: 1,display: "flex",
                justifyContent: "center", }}
              >
                Pending Projects ({projectSummary.totalPending})
              </Button>
              <Button
                variant={selectedChart === "ongoing" ? "contained" : "outlined"}
                onClick={() => setSelectedChart("ongoing")}
                sx={{ marginRight: 1,display: "flex",
                justifyContent: "center", }}
              >
                Ongoing Projects ({projectSummary.totalOngoing})
              </Button>
              <Button
                variant={
                  selectedChart === "complete" ? "contained" : "outlined"
                }
                onClick={() => setSelectedChart("complete")}
                sx={{ marginRight: 1,display: "flex",
                justifyContent: "center", }}
              >
                Completed Projects ({projectSummary.totalCompleted})
              </Button>
            </Box>
            <Divider sx={{ marginBottom: 2 }} />
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Project Name</TableCell>
                    <TableCell>Activity Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Planned Cost</TableCell>
                    <TableCell>Actual Cost</TableCell>
                    <TableCell>Cost Variance</TableCell>
                    <TableCell>Justification</TableCell>
                    <TableCell>Lessons Learned</TableCell>
                  <TableCell>Challenges</TableCell>
                    <TableCell>Comments</TableCell>
                    {actionColumn.header}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredActivityData.length > 0 ? (
                    filteredActivityData.map((activity, index) => (
                      <TableRow key={index}>
                        <TableCell>{activity.projectName?.projectName}</TableCell>
                        
                        <TableCell>{activity.activityname}</TableCell>
                        <TableCell>
                          {activity.description ? (
                            activity.description.split(" ").length > 6 ? (
                              <>
                                {activity.description.split(" ").slice(0, 6).join(" ")}...
                                <Button size="small" onClick={() => handleOpenDialog(activity.description)}>
                                  More
                                </Button>
                              </>
                            ) : (
                              activity.description
                            )
                          ) : (
                            "No description"
                          )}
                        </TableCell>
                        <TableCell>
                          {activity.dateAchieved &&
                            new Date(activity.dateAchieved).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {activity.targetDate &&
                            new Date(activity.targetDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{<StatusBadge status={activity.status} />}</TableCell>
                        <TableCell>{formatMonetaryValue(activity.target)}</TableCell>
                        <TableCell>{formatMonetaryValue(activity.current)}</TableCell>
                        <TableCell>{activity.percentageAchievement}</TableCell>
                        
                        <TableCell>
                      {activity.justify ? (
                        activity.justify.split(" ").length > 6 ? (
                          <>
                            {activity.justify.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(activity.justify)}>
                              More
                            </Button>
                          </>
                        ) : (
                          activity.justify
                        )
                      ) : (
                        "No justify"
                      )}
                    </TableCell>
                    <TableCell>
                      {activity.lessons ? (
                        activity.lessons.split(" ").length > 6 ? (
                          <>
                            {activity.lessons.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(activity.lessons)}>
                              More
                            </Button>
                          </>
                        ) : (
                          activity.lessons
                        )
                      ) : (
                        "No lessons"
                      )}
                    </TableCell>
                    <TableCell>
                      {activity.challenges ? (
                        activity.challenges.split(" ").length > 6 ? (
                          <>
                            {activity.challenges.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(activity.challenges)}>
                              More
                            </Button>
                          </>
                        ) : (
                          activity.challenges
                        )
                      ) : (
                        "No challenges"
                      )}
                    </TableCell>
                        <TableCell>
                      {activity.comments ? (
                        activity.comments.split(" ").length > 6 ? (
                          <>
                            {activity.comments.split(" ").slice(0, 6).join(" ")}...
                            <Button size="small" onClick={() => handleOpenDialog(activity.comments)}>
                              More
                            </Button>
                          </>
                        ) : (
                          activity.comments
                        )
                      ) : (
                        "No comments"
                      )}
                    </TableCell>
                        {actionColumn.cell(activity)}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No activity data available.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      default:
        return null;
    }
  };

  // Update calculateAchievement function to always return with % symbol
  const calculateAchievement = (current, target, baseline) => {
    const currentVal = parseFloat(current) || 0;
    const targetVal = parseFloat(target) || 0;
    const baselineVal = parseFloat(baseline) || 0;
    
    if (targetVal === baselineVal) return "0%";
    
    const achievement = ((currentVal) / (targetVal)) * 100;
    return `${Math.round(achievement * 100) / 100}%`;
  };

  // Replace the existing calculateCostVariance function with this new one
const calculateCostVariance = (planned, actual) => {
  if (!actual || !planned) return "0%";
  const actualValue = parseFloat(actual);
  const plannedValue = parseFloat(planned);
  if (plannedValue === 0) return "0%";
  return `${Math.round((actualValue / plannedValue) * 100)}%`;
};

  // const formatCostVariance = (value) => {
  //   if (!value) return '0%';
  //   if (typeof value === 'object') {
  //     return `${value.value}%`;
  //   }
  //   return `${value}%`;
  // };

  const renderEditDialogContent = () => {
    switch (itemToEdit?.type) {
      case "outcome":
      case "output":
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Outcome Name"
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Outcome Indicator"
              value={editFormData.indicators.join(", ")}
              onChange={(e) => setEditFormData({
                ...editFormData,
                indicators: e.target.value.split(",").map(item => item.trim())
              })}
              helperText="Enter indicators separated by commas"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Baseline Value"
                type="number"
                value={editFormData.baseline.value}
                onChange={(e) => {
                  const newBaseline = e.target.value;
                  const achievement = `${calculateAchievement(
                    editFormData.current.value,
                    editFormData.target.value,
                    newBaseline
                  )}%`; // Add % symbol
                  setEditFormData({
                    ...editFormData,
                    baseline: { ...editFormData.baseline, value: newBaseline },
                    percentageAchievement: achievement
                  });
                }}
                fullWidth
              />
              <TextField
                label="Target Value"
                type="number"
                value={editFormData.target.value}
                onChange={(e) => {
                  const newTarget = e.target.value;
                  const achievement = calculateAchievement(
                    editFormData.current.value,
                    newTarget,
                    editFormData.baseline.value
                  );
                  setEditFormData({
                    ...editFormData,
                    target: { ...editFormData.target, value: newTarget },
                    percentageAchievement: achievement.toString()
                  });
                }}
                fullWidth
              />
              <TextField
                label="Current Value"
                type="number"
                value={editFormData.current.value}
                onChange={(e) => {
                  const newCurrent = e.target.value;
                  const achievement = calculateAchievement(
                    newCurrent,
                    editFormData.target.value,
                    editFormData.baseline.value
                  );
                  setEditFormData({
                    ...editFormData,
                    current: { ...editFormData.current, value: newCurrent },
                    percentageAchievement: achievement.toString()
                  });
                }}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="date"
                label="Baseline Date"
                value={editFormData.dateAchieved}
                onChange={(e) => setEditFormData({ ...editFormData, dateAchieved: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label="Target Date"
                value={editFormData.targetDate}
                onChange={(e) => setEditFormData({ ...editFormData, targetDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Reporting Frequency</InputLabel>
              <Select
                value={editFormData.reportingFrequency}
                onChange={(e) => setEditFormData({ ...editFormData, reportingFrequency: e.target.value })}
              >
                <MenuItem value="Monthly">Monthly</MenuItem>
                <MenuItem value="Quarterly">Quarterly</MenuItem>
                <MenuItem value="Annually">Annually</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Level of Achievement"
              value={editFormData.percentageAchievement}
              InputProps={{ 
                readOnly: true,
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }}
              helperText="Automatically calculated based on (Current Value / Target Value) × 100"
            />
            <TextField
              fullWidth
              label="Justification"
              multiline
              rows={2}
              value={editFormData.justify}
              onChange={(e) => setEditFormData({ ...editFormData, justify: e.target.value })}
            />
            <TextField
              fullWidth
              label="Comments"
              multiline
              rows={2}
              value={editFormData.comments}
              onChange={(e) => setEditFormData({ ...editFormData, comments: e.target.value })}
            />
          </Box>
        );

      case "activity":
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Activity Name"
              value={editFormData.activityname}
              onChange={(e) => setEditFormData({ ...editFormData, activityname: e.target.value })}
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={3}
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="date"
                label="Start Date"
                value={editFormData.dateAchieved}
                onChange={(e) => setEditFormData({ ...editFormData, dateAchieved: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="date"
                label="End Date"
                value={editFormData.targetDate}
                onChange={(e) => setEditFormData({ ...editFormData, targetDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editFormData.status}
                onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Ongoing">Ongoing</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Planned Cost"
                type="number"
                value={editFormData.target.value}
                onChange={(e) => {
                  const newPlanned = e.target.value;
                  const variance = `${calculateCostVariance(
                    newPlanned,
                    editFormData.current.value
                  )}%`; // Add % symbol
                  setEditFormData({
                    ...editFormData,
                    target: { ...editFormData.target, value: newPlanned },
                    resourceallocated: { 
                      ...editFormData.resourceallocated, 
                      value: variance
                    }
                  });
                }}
                fullWidth
              />
              <TextField
                label="Actual Cost"
                type="number"
                value={editFormData.current.value}
                onChange={(e) => {
                  const newActual = e.target.value;
                  const variance = calculateCostVariance(
                    editFormData.target.value,
                    newActual
                  );
                  setEditFormData({
                    ...editFormData,
                    current: { ...editFormData.current, value: newActual },
                    resourceallocated: { 
                      ...editFormData.resourceallocated, 
                      value: variance.toString()
                    }
                  });
                }}
                fullWidth
              />
              <TextField
                label="Cost Variance"
                value={editFormData.resourceallocated.value}
                InputProps={{ 
                  readOnly: true,
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }}
                helperText="Automatically calculated based on (Planned Cost / Actual Cost) × 100"
                fullWidth
              />
            </Box>
            <TextField
              fullWidth
              label="Justification"
              multiline
              rows={2}
              value={editFormData.justify}
              onChange={(e) => setEditFormData({ ...editFormData, justify: e.target.value })}
            />
            <TextField
              fullWidth
              label="Lessons Learned"
              multiline
              rows={2}
              value={editFormData.lessons}
              onChange={(e) => setEditFormData({ ...editFormData, lessons: e.target.value })}
            />
            <TextField
              fullWidth
              label="Challenges"
              multiline
              rows={2}
              value={editFormData.challenges}
              onChange={(e) => setEditFormData({ ...editFormData, challenges: e.target.value })}
            />
            <TextField
              fullWidth
              label="Comments"
              multiline
              rows={2}
              value={editFormData.comments}
              onChange={(e) => setEditFormData({ ...editFormData, comments: e.target.value })}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: "100%", md: "100%" },
        margin: "auto",
        p: { xs: 1, sm: 2, md: 3 },
        overflowX: "auto",
      }}
    >
      <div  style={{  marginRight: "24px" }}>
        <h1
          style={{
            fontSize: "1.5rem",
            fontWeight: "bold",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "center",
          }}
        >
          Data Collection Dashboard
        </h1>
        <Divider sx={{ marginBottom: 2 }} />

        {error && (
          <Alert severity="error" className="mb-4">
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>
      <Button
          variant={"outlined"}
         
          component={Link} 
          to="/datadraft"
          sx={{ marginRight: 1,display: "flex",
          justifyContent: "center", }}
        >
          Draft
        </Button>
        <Button
          variant={formType === "outcome" ? "contained" : "outlined"}
          onClick={() => setFormType("outcome")}
          sx={{ marginRight: 1,display: "flex",
          justifyContent: "center", }}
        >
          Outcome
        </Button>
        <Button
          variant={formType === "output" ? "contained" : "outlined"}
          onClick={() => setFormType("output")}
          sx={{ marginRight: 1,display: "flex",
          justifyContent: "center", }}
        >
          Output
        </Button>
        <Button
          variant={formType === "activity" ? "contained" : "outlined"}
          onClick={() => setFormType("activity")}
          sx={{ marginRight: 1,display: "flex",
          justifyContent: "center", }}
        >
          Activity
        </Button>
      </Box>

        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          {renderTable()}
        </TableContainer>
      </div>

      {/* Description Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Description</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{selectedDescription}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit {itemToEdit?.type}</DialogTitle>
        <DialogContent>
          {renderEditDialogContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" color="primary">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this item?</Typography>
          {itemToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Item Details:</Typography>
              <Typography>Name: {itemToDelete.name || itemToDelete.activityname}</Typography>
              <Typography>Type: {itemToDelete.type}</Typography>
              <Typography>Project: {itemToDelete.projectName?.projectName}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => handleDelete(itemToDelete._id)} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarMessage !== ""}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
      >
        <Alert
          onClose={() => setSnackbarMessage("")}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DataCollection;